import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { countryName, HELD_STILL_KEPT, type ListImported, type ListPreview } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { priceWords } from "./FindPanel.js";

/**
 * **Import a list** — named people at companies, from a file somebody brings (23 §a list somebody brings).
 *
 * A panel behind one button on Companies, like *Find companies* on Today, because it is the same act by another road:
 * companies join the list, are read and judged, and are written to. What is different is who the letter is for — the
 * person on the list, by name, rather than the address a company prints on its contact page — and that is the whole
 * reason a business that sells to large companies brings one.
 *
 * Two presses, on purpose. *Read the list* adds nothing and costs nothing: it says what each column was read as, which
 * columns were left, who would be added and why every other row would not, and the sentence each letter's footer will
 * carry about where the address came from. Only *Add* adds, and the server reads the file again when it does rather
 * than trusting this screen.
 */

/** `groupId` starts the list in one group, as a group's own Companies tab does (38). */
export function ImportPanel({ onClose, groupId: inGroup = "" }: { onClose: () => void; groupId?: string }) {
  const setup = useQuery({ queryKey: ["customers", "setup"], queryFn: () => api.customersSetup() });
  const client = useQueryClient();

  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [source, setSource] = useState("");
  const [groupId, setGroupId] = useState(inGroup);
  const [country, setCountry] = useState<string | null>(null);
  const [preview, setPreview] = useState<ListPreview | null>(null);
  const [done, setDone] = useState<ListImported | null>(null);

  // A named person at a company is written to directly, so only a group of companies written to directly takes them.
  const groups = (setup.data?.groups ?? []).filter((g) => g.state !== "dropped" && g.audience === "business" && g.route === "direct");
  const countries = [...new Set((setup.data?.markets ?? []).map((m) => m.country))];
  // For a row that says no country: the first place this business looks in, until the person says otherwise.
  const fallback = country ?? countries[0] ?? "";
  const ask = () => ({ csv: csv ?? "", groupId, source, ...(fallback ? { country: fallback } : {}) });
  const ready = Boolean(csv) && Boolean(groupId) && source.trim().length >= 2;

  const read = useMutation({ mutationFn: () => api.previewList(ask()), onSuccess: (p) => setPreview(p) });
  const add = useMutation({
    mutationFn: () => api.importList(ask()),
    onSuccess: (d) => {
      setDone(d);
      void client.invalidateQueries({ queryKey: ["customers", "today"] });
      void client.invalidateQueries({ queryKey: ["customers", "prospects"] });
    },
  });
  // Anything changed after a preview makes it a preview of something else, so it goes.
  const changed = () => { setPreview(null); read.reset(); };

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-label="Import a list">
      <button className="flex-1 bg-ink/20" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-[680px] bg-panel border-l border-line shadow-2xl flex flex-col overflow-y-auto">
        <div className="p-5 flex flex-col gap-3.5">
          <div className="flex items-center gap-2.5">
            <h1 className="text-base font-semibold">Import a list</h1>
            <span className="text-[11.5px] text-muted">nothing joins your list until you add it</span>
            <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-page" aria-label="Close"><Icon name="x" size={14} /></button>
          </div>
          <p className="text-[12.5px] text-muted leading-relaxed">
            A CSV of named people at companies, as a contact directory exports it. Each one is written to by their first
            name, at the address on the list. Only a company's own address is kept, only a verified one when the list
            says, and never one that asked you to stop.
          </p>

          {done ? (
            <div className="rounded-xl border border-line bg-page p-4 flex flex-col gap-2" role="status">
              <p className="text-[13px] font-semibold flex items-center gap-2">
                <Icon name="check" size={14} className="text-good" /> Added {done.added} {done.added === 1 ? "person" : "people"}
                {done.skipped ? <span className="font-normal text-muted">· {done.skipped} left out</span> : null}
              </p>
              <p className="text-[12.5px] text-muted leading-relaxed">
                Each company is being read now, on its own site. Where it is worth writing to, its letter is written and
                waits on Today for a yes. Nothing is sent until the owner presses Send.
              </p>
              <button onClick={onClose} className="self-start px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold">Done</button>
            </div>
          ) : (
            <>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">The list</span>
                <input
                  type="file" accept=".csv,text/csv,text/plain" aria-label="The list"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    changed();
                    setFileName(file?.name ?? "");
                    setCsv(file ? await file.text() : null);
                  }}
                  className="text-[12.5px]"
                />
                {fileName ? <span className="text-[11px] text-faint">{fileName}</span> : null}
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Where the list comes from</span>
                <input
                  value={source} onChange={(e) => { setSource(e.target.value); changed(); }} maxLength={40}
                  aria-label="Where the list comes from" placeholder="the directory's name, as each letter will say it"
                  className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px]"
                />
                <span className="text-[11px] text-faint leading-relaxed">
                  Every letter to these people says, in its footer, that their address is listed in it.
                </span>
              </label>

              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Who we look for</span>
                <div className="flex flex-wrap gap-1.5">
                  {groups.map((g) => (
                    <button
                      key={g.id} onClick={() => { setGroupId(g.id === groupId ? "" : g.id); changed(); }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${groupId === g.id ? "bg-ink text-white border-ink" : "bg-page border-line-2 text-ink-2"}`}
                    >{g.name}</button>
                  ))}
                  {!groups.length && <span className="text-xs text-muted">No group of companies you write to directly yet — New group, in the menu, makes one.</span>}
                </div>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">A row that does not say its country</span>
                <select
                  value={fallback} onChange={(e) => { setCountry(e.target.value); changed(); }} aria-label="Country for a row that does not say"
                  className="self-start px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px]"
                >
                  <option value="">leave it out</option>
                  {countries.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                </select>
              </label>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => read.mutate()} disabled={!ready || read.isPending}
                  className="px-3 py-2 rounded-lg bg-ink text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
                ><Icon name="search" size={14} /> {read.isPending ? "Reading…" : "Read the list"}</button>
                <span className="text-xs text-muted">Free. Nothing is added yet.</span>
              </div>
              {read.error ? <p className="text-xs text-bad" role="alert">{String(read.error)}</p> : null}

              {preview && <Preview p={preview} onAdd={() => add.mutate()} adding={add.isPending} />}
              {add.error ? <p className="text-xs text-bad" role="alert">{String(add.error)}</p> : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** What an import would do, before it does any of it. */
function Preview({ p, onAdd, adding }: { p: ListPreview; onAdd: () => void; adding: boolean }) {
  return (
    <div className="rounded-xl border border-line bg-page overflow-hidden flex flex-col">
      <div className="flex flex-wrap items-center gap-2 px-3.5 py-2 border-b border-line">
        <span className="text-[12.5px] font-semibold">{p.adds} to add</span>
        <span className="text-[11px] text-muted">of {p.rows} {p.rows === 1 ? "row" : "rows"} read</span>
        <button
          onClick={onAdd} disabled={!p.adds || adding}
          className="ml-auto px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-40"
        >{adding ? "Adding…" : `Add ${p.adds}`}</button>
      </div>

      <div className="px-3.5 py-2.5 border-b border-line flex flex-col gap-1.5 text-[12px]">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Read as</span>
        <span className="text-muted leading-relaxed">
          {p.read.map((r) => <span key={r.field} className="mr-3 inline-block"><b className="text-ink">{r.says}</b> ← {r.columns.join(", ")}</span>)}
        </span>
        {p.ignored.length > 0 && (
          <span className="text-muted leading-relaxed"><b className="text-ink">Not read:</b> {p.ignored.join(", ")}</span>
        )}
      </div>

      {p.skipped.length > 0 && (
        <div className="px-3.5 py-2.5 border-b border-line flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Left out, and why</span>
          {p.skipped.map((s) => (
            <div key={s.why} className="flex flex-col gap-0.5">
              <span className="text-[12px]"><b className="tabular-nums">{s.count}</b> · {s.says}</span>
              <span className="text-[11px] text-faint">{s.examples.join(" · ")}</span>
            </div>
          ))}
        </div>
      )}

      {p.held.length > 0 && (
        <div className="px-3.5 py-2.5 border-b border-line flex flex-col gap-1" role="note">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Added, and no letter</span>
          {p.held.map((h) => (
            <span key={h.country} className="text-[12px] leading-relaxed">
              <b className="tabular-nums">{h.count}</b> · {countryName(h.country)}: {h.says} {HELD_STILL_KEPT}
            </span>
          ))}
        </div>
      )}

      {p.add.length > 0 && (
        <table className="w-full text-[12px]">
          <tbody>
            {p.add.map((r) => (
              <tr key={r.row} className="border-b border-line last:border-0">
                <td className="px-3.5 py-1.5"><span className="font-semibold">{r.person || "—"}</span>{r.role ? <span className="text-muted"> · {r.role}</span> : null}</td>
                <td className="px-2 py-1.5 text-muted">{r.company}</td>
                <td className="px-2 py-1.5 text-muted">{r.email}</td>
                <td className="px-3.5 py-1.5 text-faint">{r.country}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {p.adds > p.add.length && <p className="px-3.5 py-1.5 text-[11px] text-faint border-t border-line">and {p.adds - p.add.length} more</p>}

      <div className="px-3.5 py-2.5 border-t border-line flex flex-col gap-1.5 text-[11.5px] text-muted leading-relaxed">
        {p.footer && <span>Each letter's footer will say: <i>“{p.footer}”</i></span>}
        <span>
          Free to import. Each company is then read on its own site and judged as a found one is, and a letter is written for
          each one worth writing to{p.perCompanyUsd ? <> — {priceWords(p.perCompanyUsd)} a company</> : null}. Nothing is sent until the owner presses Send.
        </span>
      </div>
    </div>
  );
}
